// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import { useMemo } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox } from "./SectionBox";
import { DocumentFrame } from "./DocumentFrame";

// ─────────────────────────────────────────────────────────────────────────────
// EL HADEGEL BOARD — design comparison surface for the growth engine.
// Gray canvas (matches the Marketing Board). Grouped by DEVICE:
//   • DESKTOP row — their site (elhadegel.co.il) next to ours (elhadegel-web),
//     then the rest of our funnel screens.
//   • MOBILE row (below, with space) — same order, phone frames.
// So their site and ours sit side by side in each device group; a clear gap
// separates the desktop group from the mobile group.
// Target for the redesign: their brand DNA (navy #021531, olive-gold Magen
// David #A2AF6E, Danidin display, flag-triangle motif; map dots → flags).
// Captures live in-repo at public/board/elhadegel/{site}-{screen}-{desktop|mobile}.jpg
// Re-capture:  node scripts/capture-elhadegel-board.mjs
// ─────────────────────────────────────────────────────────────────────────────

const T = { bg: "#E5E5E5", dots: "#CFCFCF", ink: "#1C1917", ink2: "#57534E", line: "#EADDD0", cream: "#FDF7F2", white: "#FFFFFF" };
const EH = { navy: "#021531", gold: "#A2AF6E", blue: "#0059FF", band: "#005BB6" };
const FONT = "Inter, system-ui, -apple-system, sans-serif";

const bust = "?v=2026-07-20";
const IMG = (name: string) => `/board/elhadegel/${name}.jpg${bust}`;

const DESK_W = 680;
const PHONE_W = 290;
const DESK_STEP = DESK_W + 140;      // horizontal step between desktop frames
const PHONE_STEP = PHONE_W + 20 + 130; // between phone frames

// The screens, in row order: THEIR site first, then OUR site, then our funnel.
type Frame = { label: string; sub: string; file: string; url: string; ref?: boolean };
const FRAMES: Frame[] = [
  { label: "Their site", sub: "Home", file: "their-home", url: "elhadegel.co.il", ref: true },
  { label: "Our site", sub: "Landing", file: "ours-01-home", url: "elhadegel-web.pages.dev/" },
  { label: "Our site", sub: "Welcome / invite", file: "ours-02-welcome", url: "elhadegel-web.pages.dev/welcome" },
  { label: "Our site", sub: "Google sign-in", file: "ours-03-login", url: "elhadegel-web.pages.dev/login" },
  { label: "Our site", sub: "Share", file: "ours-04-share", url: "elhadegel-web.pages.dev/share" },
  { label: "Our site", sub: "Status", file: "ours-05-status", url: "elhadegel-web.pages.dev/status" },
];

// ── Nodes ────────────────────────────────────────────────────────────────────

function ZoneTitleNode({ data }: { data: { title: string; sub?: string; tag?: string } }) {
  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 150, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.03em", color: T.ink }}>{data.title}</div>
        {data.tag && (
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "0.05em", color: T.white, background: T.ink, borderRadius: 999, padding: "9px 22px", whiteSpace: "nowrap" }}>{data.tag}</span>
        )}
      </div>
      {data.sub && <div style={{ marginTop: 18, fontSize: 30, fontWeight: 500, color: T.ink2, letterSpacing: "-0.01em", maxWidth: 1300 }}>{data.sub}</div>}
    </div>
  );
}

// SECTION CONTAINER — the board standard (Erez-locked 2026-07-19): a darker-gray
// rounded box that holds a whole section, its title INSIDE at the top and all the
// section's cards organized within. Pushed BEFORE its cards so it sits behind them.
function ZoneContainer({ data }: { data: { title: string; sub?: string; tag?: string; w: number; h: number } }) {
  return (
    <div style={{ width: data.w, height: data.h, boxSizing: "border-box", background: "#D6D3CD", border: "1px solid #C6C2BA", borderRadius: 36, padding: "44px 52px", fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 60, fontWeight: 800, letterSpacing: "-0.02em", color: T.ink }}>{data.title}</div>
        {data.tag && <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: "0.05em", color: T.white, background: T.ink, borderRadius: 999, padding: "8px 20px", whiteSpace: "nowrap" }}>{data.tag}</span>}
      </div>
      {data.sub && <div style={{ marginTop: 14, fontSize: 26, fontWeight: 500, color: T.ink2, letterSpacing: "-0.01em" }}>{data.sub}</div>}
    </div>
  );
}

// label above a frame: site name + screen, with a REF/OURS chip
function FrameLabelNode({ data }: { data: { label: string; sub: string; ref?: boolean } }) {
  return (
    <div style={{ fontFamily: FONT, display: "flex", alignItems: "baseline", gap: 10 }}>
      <span style={{ fontSize: 26, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em" }}>{data.label}</span>
      <span style={{ fontSize: 20, fontWeight: 500, color: T.ink2 }}>· {data.sub}</span>
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", color: data.ref ? "#2B5560" : "#9A6A2B", background: data.ref ? "rgba(43,85,96,0.12)" : "rgba(154,106,43,0.14)", borderRadius: 999, padding: "3px 10px" }}>{data.ref ? "REFERENCE" : "OURS"}</span>
    </div>
  );
}

function DesktopFrameNode({ data }: { data: { url: string; img: string; w: number } }) {
  return (
    <div style={{ fontFamily: FONT, width: data.w }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, height: 40, padding: "0 14px", borderTopLeftRadius: 12, borderTopRightRadius: 12, border: `1px solid ${T.line}`, borderBottom: "none", background: T.white }}>
        <span style={{ display: "flex", gap: 6 }}>
          <i style={{ width: 10, height: 10, borderRadius: "50%", background: "#F87171", display: "inline-block" }} />
          <i style={{ width: 10, height: 10, borderRadius: "50%", background: "#FBBF24", display: "inline-block" }} />
          <i style={{ width: 10, height: 10, borderRadius: "50%", background: "#34D399", display: "inline-block" }} />
        </span>
        <span style={{ flex: 1, textAlign: "center", fontSize: 12, color: T.ink2, background: T.bg, borderRadius: 7, padding: "5px 10px", marginRight: 36, direction: "ltr", fontFamily: "ui-monospace, monospace" }}>{data.url}</span>
      </div>
      <div style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12, overflow: "hidden", border: `1px solid ${T.line}`, background: T.cream, boxShadow: "0 20px 50px rgba(0,0,0,0.16)", fontSize: 0, lineHeight: 0 }}>
        <img src={data.img} alt={data.url} draggable={false} style={{ width: data.w, height: "auto", display: "block" }} />
      </div>
    </div>
  );
}

function PhoneFrameNode({ data }: { data: { img: string; w: number } }) {
  const bezel = 9;
  return (
    <div style={{ fontFamily: FONT, width: data.w + bezel * 2 }}>
      <div style={{ padding: bezel, background: "#1C1917", borderRadius: 38, boxShadow: "0 20px 50px rgba(0,0,0,0.22)" }}>
        <div style={{ borderRadius: 29, overflow: "hidden", background: T.cream, fontSize: 0, lineHeight: 0 }}>
          <img src={data.img} alt="mobile" draggable={false} style={{ width: data.w, height: "auto", display: "block" }} />
        </div>
      </div>
    </div>
  );
}

function BrandDnaNode() {
  const swatches = [
    { name: "Navy field", hex: EH.navy, fg: "#FFFFFF" },
    { name: "Magen-gold", hex: EH.gold, fg: "#0A1B3D" },
    { name: "Donate blue", hex: EH.blue, fg: "#FFFFFF" },
    { name: "Band blue", hex: EH.band, fg: "#FFFFFF" },
  ];
  return (
    <SectionBox title="Brand DNA" layout="custom">
    <div style={{ fontFamily: FONT, width: 620, background: T.white, border: `1px solid ${T.line}`, borderRadius: 18, boxShadow: "0 20px 50px rgba(0,0,0,0.14)", padding: 40, color: T.ink }}>
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", color: "#9A9188", marginBottom: 12 }}>PALETTE</div>
      <div style={{ display: "flex", gap: 10, marginBottom: 22 }}>
        {swatches.map((s) => (
          <div key={s.name} style={{ flex: 1 }}>
            <div style={{ height: 58, borderRadius: 10, background: s.hex, border: `1px solid ${T.line}`, display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 6 }}>
              <span style={{ fontSize: 10, color: s.fg, fontWeight: 600 }}>{s.hex}</span>
            </div>
            <div style={{ fontSize: 11, color: T.ink2, marginTop: 6, textAlign: "center" }}>{s.name}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#9A9188", marginBottom: 8 }}>TYPE</div>
          <div style={{ fontSize: 14.5 }}>Danidin Condensed — display</div>
          <div style={{ fontSize: 13, color: T.ink2, marginTop: 4 }}>Danidin — body · RTL Hebrew</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#9A9188", marginBottom: 8 }}>MOTIF</div>
          <div style={{ fontSize: 14.5 }}>Two-tone Magen David + flag/pennant triangles</div>
        </div>
      </div>
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.line}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#9A9188", marginBottom: 8 }}>THE GAP</div>
        <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.5 }}>
          Theirs: <b style={{ color: T.ink }}>navy, gold Magen David, Danidin, flag triangles</b>. Ours today: <b style={{ color: T.ink }}>light / generic sans, blue accents, map dots</b>. The redesign closes it, and the dots become flags.
        </div>
      </div>
    </div>
    </SectionBox>
  );
}

// SECTION — the board-standard container (Erez-locked 2026-07-19). THE METHOD:
// a neutral darker-gray box that HUGS its cards. `display:inline-block` + a flex
// row of the cards + equal `padding` on all sides means CSS wraps the content
// perfectly (auto width AND auto height — a tall card just makes the box taller);
// no guessed dimensions. Title sits INSIDE at the top.
function SectionNode({ data }: { data: { title: string; device: "desktop" | "phone"; frames: Frame[] } }) {
  const w = data.device === "desktop" ? DESK_W : PHONE_W;
  return (
    <SectionBox title={data.title}>
      {data.frames.map((f) => (
        data.device === "desktop"
          ? <DesktopFrameNode key={f.file} data={{ url: f.url, img: IMG(`${f.file}-desktop`), w }} />
          : <PhoneFrameNode key={f.file} data={{ img: IMG(`${f.file}-mobile`), w }} />
      ))}
    </SectionBox>
  );
}

// A KB page PRESENTED on the board (root CLAUDE.md, "WHAT A BOARD IS MADE OF").
// The document is a page_content row: an agent edits it with update_kb_page and
// the board reflects it with no deploy.
function DocsNode({ data }: { data: { title: string; docs: { slug: string; label: string }[] } }) {
  return (
    <SectionBox title={data.title}>
      {data.docs.map((d) => (
        <DocumentFrame key={d.slug} slug={d.slug} label={d.label} width={980} height={1400} />
      ))}
    </SectionBox>
  );
}

const nodeTypes: NodeTypes = {
  section: SectionNode as unknown as NodeTypes[string],
  brandDna: BrandDnaNode as unknown as NodeTypes[string],
  docs: DocsNode as unknown as NodeTypes[string],
};

function buildNodes(): Node[] {
  // Each section is ONE self-hugging box. Placed side by side to read as a tidy
  // rectangle; Brand DNA sits below the (taller) desktop column.
  return [
    { id: "desk-section", type: "section", position: { x: 0, y: 0 }, draggable: false, data: { title: "Desktop", device: "desktop", frames: FRAMES } },
    { id: "mob-section", type: "section", position: { x: 4950, y: 0 }, draggable: false, data: { title: "Mobile", device: "phone", frames: FRAMES } },
    { id: "brand-dna", type: "brandDna", position: { x: 0, y: 4980 }, draggable: false, data: {} },
    // ADDED 2026-08-02 — a board is cumulative; nothing above was removed.
    {
      id: "docs-section",
      type: "docs",
      position: { x: 1700, y: 4980 },
      draggable: false,
      data: {
        title: "Review",
        docs: [{ slug: "kb-doc/el-hadegel/david-photo-options", label: "דוד שרז — photo options" }],
      },
    },
    // ADDED 2026-08-31 — a board is cumulative; nothing above was removed.
    // The week the referral engine went to zero. Written as a KB page and
    // PRESENTED here, per "WHAT A BOARD IS MADE OF" — the document lives in
    // page_content, so it can be corrected with update_kb_page and no deploy.
    {
      id: "growth-section",
      type: "docs",
      position: { x: 3400, y: 4980 },
      draggable: false,
      data: {
        title: "Growth",
        docs: [{ slug: "kb-doc/el-hadegel/growth-week-2026-08-31", label: "Week to 31 Aug — the referral engine went to zero" }],
      },
    },
    // ADDED 2026-09-01 — a board is cumulative; nothing above was removed.
    // WHO stopped, not just what. Two supporters carried ~half of every week's
    // referred signups all August and both fell ~95% after the 26th; the tail of
    // ~30 daily sharers fell to ~9. Presented, not typed into this file.
    {
      id: "who-stopped-section",
      type: "docs",
      position: { x: 5100, y: 4980 },
      draggable: false,
      data: {
        title: "Who stopped",
        docs: [{ slug: "kb-doc/el-hadegel/who-stopped-sharing", label: "The distribution was two people" }],
      },
    },
  ];
}

export function ElHaDegelBoardPage() {
  const nodes = useMemo(() => buildNodes(), []);
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.15} />
    </div>
  );
}
